"use client";
import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../store/authstore";

type InFeedAdProps = {
  className?: string;
  style?: React.CSSProperties;
  adClient?: string;
  adSlot?: string;
};

export default function InFeedAd({ 
  className, 
  style, 
  adClient = "ca-pub-1374032065732962", 
  adSlot = "2090057461" // Using same slot as DisplayAd that works
}: InFeedAdProps) {
  const adRef = useRef<HTMLModElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isPremium = Boolean(user?.premium_data?.premium === "1");

  // Intersection Observer for lazy loading - better performance
  // Do not load ads for premium users
  useEffect(() => {
    if (isPremium) return;
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasInitialized.current) {
            setIsVisible(true);
          }
        });
      },
      { rootMargin: '200px' } // Load ad 200px before it becomes visible
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [isPremium]);

  useEffect(() => {
  if (isPremium) return;
  if (!isVisible || hasInitialized.current) return;
    if (typeof window === "undefined") return;

    const initAd = () => {
      if (!adRef.current || !containerRef.current) return false;
      
      if (typeof window.adsbygoogle === 'undefined') {
        // console.log('InFeedAd: AdSense not loaded yet');
        return false;
      }
      
      const adStatus = adRef.current.getAttribute('data-adsbygoogle-status');
      if (adStatus === 'done') {
        // console.log('InFeedAd: Already initialized');
        return true;
      }

      const containerWidth = containerRef.current.offsetWidth;
      // console.log('InFeedAd: Container width:', containerWidth);
      
      if (containerWidth === 0) {
        // console.log('InFeedAd: Container has no width');
        return false;
      }

      try {
        hasInitialized.current = true;
        // console.log('InFeedAd: Pushing to adsbygoogle');
        
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          // console.log('InFeedAd: ✅ Ad pushed successfully');
        } catch (err) {
          const error = err as Error;
          // console.log('InFeedAd: Error:', error?.name, error?.message);
          if (error?.name !== 'TagError') {
            console.warn('InFeedAd:', error?.message);
          }
        }
        return true;
      } catch {
        return true;
      }
    };

    const attempts = [200, 500, 1000, 2000, 3000];
    const timers: NodeJS.Timeout[] = [];

    attempts.forEach((delay) => {
      const timer = setTimeout(() => {
        if (initAd()) {
          timers.forEach(t => clearTimeout(t));
        }
      }, delay);
      timers.push(timer);
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [isVisible, isPremium]);

  // If premium user show placeholder
  if (isPremium) {
    return (
      <div 
        ref={containerRef} 
        className={className} 
        style={{ width: '100%', display: 'block', overflow: 'hidden', maxHeight: '250px', textAlign: 'center', ...style }}
      />
    );
  }

  return (
    <div 
      ref={containerRef} 
      className={className} 
      style={{ 
        width: '100%',
        display: 'block',
        overflow: 'hidden',
        maxHeight: '250px',
        textAlign: 'center',
        ...style 
      }}
    >
      <ins 
        ref={adRef}
        className="adsbygoogle"
        style={{ 
          display: 'block',
          width: '100%',
          height: '200px',
          margin: '0 auto'
        }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
